import React from "react";
import { T } from "@transifex/react";
import styled from "styled-components";
import type { ClinicalTrialMatch } from "./use-clinical-trial-matches";

const TrialsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 8px 0;
`;

const MatchExplanation = styled.p`
  font-size: 13px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
`;

const TrialCard = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px 16px;
  background: #fafafa;
`;

const TrialTitle = styled.a`
  font-size: 15px;
  font-weight: 600;
  color: #1a56db;
  text-decoration: none;
  display: block;
  margin-bottom: 4px;

  &:hover {
    text-decoration: underline;
  }
`;

const TrialMeta = styled.div`
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 6px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
`;

const StatusBadge = styled.span<{ $recruiting: boolean }>`
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  background: ${(p) => (p.$recruiting ? "#d1fae5" : "#fef3c7")};
  color: ${(p) => (p.$recruiting ? "#065f46" : "#92400e")};
`;

const TrialSummary = styled.p`
  font-size: 13px;
  color: #374151;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const MatchScoreBar = styled.div`
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ScoreTrack = styled.div`
  flex: 1;
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
`;

const ScoreFill = styled.div<{ $percent: number }>`
  height: 100%;
  width: ${(p) => p.$percent}%;
  background: ${(p) => (p.$percent >= 70 ? "#059669" : p.$percent >= 40 ? "#d97706" : "#9ca3af")};
  border-radius: 3px;
  transition: width 0.3s ease;
`;

const ScoreLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  min-width: 36px;
  text-align: right;
`;

interface ClinicalTrialsListProps {
  trials: ClinicalTrialMatch[];
}

function isRecruiting(status: string): boolean {
  return status === "RECRUITING" || status === "ENROLLING_BY_INVITATION";
}

function formatStatus(status: string): string {
  return status.toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export const ClinicalTrialsList: React.FC<ClinicalTrialsListProps> = ({
  trials,
}) => {
  if (trials.length === 0) {
    return (
      <TrialsContainer>
        <p style={{ color: "#6b7280", fontSize: 14 }}>
          <T _str="No matching clinical trials found at this time." />
        </p>
      </TrialsContainer>
    );
  }

  const MAX_DISPLAYED = 6;
  const sorted = [...trials].sort(
    (a, b) => (b.matchScore ?? 0) - (a.matchScore ?? 0)
  );
  const displayed = sorted.slice(0, MAX_DISPLAYED);
  const hasMore = trials.length > MAX_DISPLAYED;

  return (
    <TrialsContainer data-testid="ClinicalTrialsList">
      <MatchExplanation>
        <T _str="Match scores indicate how closely a trial's eligibility criteria align with your health profile. A higher percentage means a stronger potential fit. Please discuss any trials of interest with your care team." />
      </MatchExplanation>
      {displayed.map((trial) => {
        const scorePercent = Math.round((trial.matchScore ?? 0) * 100);
        return (
          <TrialCard key={trial.nctId} data-testid={`ClinicalTrialCard-${trial.nctId}`}>
            <TrialTitle
              href={`https://clinicaltrials.gov/study/${trial.nctId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {trial.title}
            </TrialTitle>
            <TrialMeta>
              <StatusBadge $recruiting={isRecruiting(trial.status)}>
                {formatStatus(trial.status)}
              </StatusBadge>
              {trial.phase && <span>{trial.phase}</span>}
              {trial.sponsor && <span>· {trial.sponsor}</span>}
              <span>
                ·{" "}
                <a
                  href={`https://clinicaltrials.gov/study/${trial.nctId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#1a56db" }}
                >
                  {trial.nctId}
                </a>
              </span>
            </TrialMeta>
            {trial.briefSummary && (
              <TrialSummary>{trial.briefSummary}</TrialSummary>
            )}
            <MatchScoreBar>
              <ScoreTrack>
                <ScoreFill $percent={scorePercent} />
              </ScoreTrack>
              <ScoreLabel>{scorePercent}%</ScoreLabel>
            </MatchScoreBar>
          </TrialCard>
        );
      })}
      {hasMore && (
        <MatchExplanation>
          <T
            _str="Showing the top {count} of {total} matched trials. Speak with your healthcare provider to learn more about additional options."
            count={MAX_DISPLAYED}
            total={trials.length}
          />
        </MatchExplanation>
      )}
    </TrialsContainer>
  );
};
